import { useContactDetailsViews, useEnsureBlockId } from "@tellescope/contact-details";
import { useAuth, useTellescopeQuery } from "@tellescope/tanstack-query";
import { MessageSquare, Phone } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildTakeoverPath, matchTakeoverPath } from "../../../pages/contacts/takeover-path";
import { useCalendarShallowStore, useCallingShallowStore, useSidebarShallowStore } from "../../../store";
import { useAppPermissionQuery, useOrgFeatures } from "../../../permissions";
import { buildCalendarPath } from "../../../pages/calendar/build-calendar-path";

export interface ContactDetailsSidebarProps {
  enduserId: string;
  viewId: string | undefined;
  setViewId: Dispatch<SetStateAction<string | undefined>>;
  closeSidebar: () => void;
}

export const useContactDetailsSidebar = ({ enduserId, viewId, setViewId }: ContactDetailsSidebarProps) => {
  const { t } = useTranslation("contacts");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ensureBlockId = useEnsureBlockId();
  const { currentUser, session } = useAuth();
  const hasTwilioAccess = !!(currentUser.twilioNumber || session.userInfo.orgTwilioNumber);
  const { openDialer } = useCallingShallowStore("openDialer");
  const { lastSelectedViewType, lastSelectedViewId } = useCalendarShallowStore(
    "lastSelectedViewType",
    "lastSelectedViewId"
  );

  const {
    views,
    isLoading: viewsAreLoading,
    viewId: resolvedViewId,
    defaultViewId,
    resolveSelection,
  } = useContactDetailsViews({ selectedViewId: viewId });

  const selectViewId = useCallback(
    (newVal: SetStateAction<string | undefined>) => {
      setViewId((prev) => resolveSelection(typeof newVal === "function" ? newVal(prev) : newVal));
    },
    [resolveSelection, setViewId]
  );

  const { data: enduser } = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: enduserId,
    enabled: !!enduserId,
  });

  const enduserHasPhone = !!enduser?.phone;

  const enduserInboxRoute = useMemo(() => {
    const params = new URLSearchParams({
      "composeTo.enduserId": enduserId,
    });
    if (!enduser?.alternatePhones?.length && enduser?.phone) {
      params.set("composeTo.number", enduser?.phone ?? "");
    }
    return `/messaging/inbox/sms/new?${params.toString()}`;
  }, [enduser?.alternatePhones?.length, enduser?.phone, enduserId]);

  const { isFeatureEnabled } = useOrgFeatures();
  const { update: updateObservationsPermission } = useAppPermissionQuery({
    model: "enduser_observations",
    actions: ["update"],
  });
  const hasCallingAccess = isFeatureEnabled("call");
  const hasMessagingAccess = isFeatureEnabled("messaging");

  const { setContentTypeData, contentTypeData } = useSidebarShallowStore("setContentTypeData", "contentTypeData");
  const blocksToHide = useMemo(() => {
    const takeover = matchTakeoverPath(pathname);
    if (!takeover || takeover.viewId !== resolvedViewId) return undefined;
    return [{ id: takeover.blockId, index: -1 }];
  }, [pathname, resolvedViewId]);

  const menuItems = useMemo(
    () => [
      {
        icon: Phone,
        label: t("Call"),
        onClick: () => openDialer({ enduser }),
        hidden: !enduserHasPhone || !hasTwilioAccess || !hasCallingAccess,
        className: "w-full justify-start",
      },
      {
        icon: MessageSquare,
        label: t("Message"),
        onClick: {
          to: enduserInboxRoute,
        },
        hidden: !enduserHasPhone || !hasTwilioAccess || !hasMessagingAccess,
        className: "w-full justify-start",
      },
    ],
    [enduser, enduserHasPhone, enduserInboxRoute, hasCallingAccess, hasMessagingAccess, hasTwilioAccess, openDialer, t]
  );

  const onMaximizeBlock = async ({ blockId, blockIndex }: { blockId?: string; blockIndex: number }) => {
    if (!resolvedViewId) return;

    const resolvedBlockId = await ensureBlockId({ viewId: resolvedViewId, blockId, blockIndex });
    if (!resolvedBlockId) return;

    navigate(buildTakeoverPath({ enduserId, viewId: resolvedViewId, blockId: resolvedBlockId }));
  };

  const onOpenTicket = (ticketId: string) => ({ to: `/tickets/details/${ticketId}` });

  const onOpenEvent = (eventId: string, startTimeInMS: number) => ({
    to: `${buildCalendarPath({
      viewType: lastSelectedViewType,
      fallbackViewType: lastSelectedViewType,
      fallbackViewId: lastSelectedViewId,
      date: new Date(startTimeInMS),
    })}?eventId=${eventId}`,
  });

  const onChangeEnduser = (nextEnduserId: string) =>
    setContentTypeData({
      ContactDetails: {
        ...contentTypeData.ContactDetails,
        enduserId: nextEnduserId,
      },
    });

  return {
    views,
    viewsAreLoading,
    resolvedViewId,
    defaultViewId,
    selectViewId,
    menuItems,
    blocksToHide,
    isFeatureEnabled,
    canUpdateObservations: updateObservationsPermission.access,
    onMaximizeBlock,
    onOpenTicket,
    onOpenEvent,
    onChangeEnduser,
  };
};
